import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';

export default function SettingsPage() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery<Record<string, string>>({
    queryKey: ['settings'],
    queryFn: () => api.get('/v1/admin/settings'),
  });

  const { register, handleSubmit } = useForm({
    values: settings || {},
  });

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, string>) => api.put('/v1/admin/settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Settings saved');
    },
  });

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">Settings</h1>

      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-medium">Site Information</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700">Site Title</label>
            <input type="text" {...register('site_title')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Tagline</label>
            <input type="text" {...register('site_tagline')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
            <input type="email" {...register('contact_email')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Footer Text</label>
            <input type="text" {...register('footer_text')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-medium">Homepage</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hero Title</label>
            <input type="text" {...register('homepage_hero_title')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hero Subtitle</label>
            <input type="text" {...register('homepage_hero_subtitle')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-medium">Social</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700">Instagram URL</label>
            <input type="url" {...register('social_instagram')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Facebook URL</label>
            <input type="url" {...register('social_facebook')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <h2 className="text-lg font-medium">Integrations</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700">Acuity Embed URL</label>
            <input type="url" {...register('acuity_embed_url')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" className="px-4 py-2 bg-brand-bronze text-white rounded-md">Save Settings</button>
        </div>
      </form>
    </div>
  );
}
